import { pickLocale, storedLocale } from "./preferences.js";
import { locales } from "@hlt/contracts";
import i18next from "i18next";
import resourcesToBackend from "i18next-resources-to-backend";
import { initReactI18next, useTranslation } from "react-i18next";
import { loadLocaleMessages } from "@hlt/i18n/browser";
// src/i18n/setup.ts
const localeNames = {
	en: "English",
	de: "Deutsch",
	es: "Español",
	fr: "Français",
	tr: "Türkçe"
};
/**
* A language's name in the interface language, for use inside sentences ("translated from Englisch").
* `localeNames` (each language's own name) stays the label in language pickers.
*/
function useLanguageName() {
	const { i18n } = useTranslation();
	let names = null;
	try {
		names = new Intl.DisplayNames([i18n.language], { type: "language" });
	} catch {}
	return (locale) => names?.of(locale) ?? localeNames[locale];
}
function isLocale(value) {
	return locales.includes(value ?? "");
}
/**
* The interface language before the profile is known: an explicit choice made in any HLT app, then the
* browser's preferred languages (what it sends as Accept-Language), then English. Detection is not stored.
*/
function initialLocale() {
	return pickLocale(locales);
}
/**
* The language after sign-in: an explicit browser choice still wins; otherwise the account's language
* (chosen on another device) applies.
*/
function sessionLocale(account) {
	return pickLocale(locales, { account: account ?? null });
}
/**
* Interface strings for `locale`. Only that language is downloaded and the returned promise resolves once it
* is ready, so apps await it before the first render. Every key exists in every language (parity test in
* @hlt/i18n), so there is no fallback language to load. The document language is set at once.
*/
async function createI18n(locale) {
	document.documentElement.lang = locale;
	const instance = i18next.createInstance();
	await instance.use(resourcesToBackend(async (language) => {
		if (!isLocale(language)) return null;
		return await loadLocaleMessages(language).catch(() => loadLocaleMessages("en"));
	})).use(initReactI18next).init({
		lng: locale,
		supportedLngs: locales,
		load: "currentOnly",
		fallbackLng: false,
		interpolation: { escapeValue: false },
		returnNull: false
	});
	return instance;
}
/** Show `locale` in this frame (interface strings and the document language) without recording a choice. */
function showLocale(instance, locale) {
	instance.changeLanguage(locale);
	document.documentElement.lang = locale;
}
/** True when the person picked a language in this browser (it then outranks the account language). */
const hasLocaleChoice = () => isLocale(storedLocale());
const useT = () => useTranslation().t;
export { createI18n, hasLocaleChoice, initialLocale, isLocale, localeNames, sessionLocale, showLocale, useLanguageName, useT };

//# sourceMappingURL=i18n.js.map